"use client";

import { FaVolumeXmark, FaVolumeLow, FaVolumeHigh } from "react-icons/fa6";
import { useIntl } from "react-intl";

interface Props {
  muted: boolean;
  volume: number;
  onMuteToggle: () => void;
  onVolumeChange: (v: number) => void;
}

/**
 * Mute button of the hero film, with a volume slider that slides out beside it on hover
 * and whenever either control has keyboard focus (so the slider is never focused while
 * invisible). Touch screens get the mute button only: they have hardware volume keys, a
 * hover-revealed slider never shows there, and iOS ignores a page-set volume anyway.
 */
export default function VideoVolumeButton({
  muted,
  volume,
  onMuteToggle,
  onVolumeChange,
}: Props) {
  const intl = useIntl();

  const displayVolume = muted ? 0 : volume;
  const Icon =
    displayVolume === 0
      ? FaVolumeXmark
      : displayVolume < 0.5
        ? FaVolumeLow
        : FaVolumeHigh;

  return (
    <div className="group/vol flex items-center gap-1 shrink-0">
      <div className="flex w-0 items-center overflow-hidden opacity-0 transition-all duration-200 group-hover/vol:w-20 group-hover/vol:opacity-100 group-focus-within/vol:w-20 group-focus-within/vol:opacity-100 pointer-coarse:hidden">
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={displayVolume}
          onChange={(e) => onVolumeChange(Number(e.target.value))}
          className="h-6 w-20 cursor-pointer accent-white"
          aria-label={intl.formatMessage({ id: "a11y.video.volume" })}
        />
      </div>

      <button
        type="button"
        onClick={onMuteToggle}
        aria-label={intl.formatMessage({
          id: muted ? "a11y.video.unmute" : "a11y.video.mute",
        })}
        className="flex h-8 w-8 items-center justify-center rounded-full text-white/90 hover:text-white hover:scale-110 transition-all pointer-coarse:h-11 pointer-coarse:w-11"
      >
        <Icon aria-hidden="true" className="text-sm" />
      </button>
    </div>
  );
}
